import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users, ClipboardList } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showJoinClass, setShowJoinClass] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  useEffect(() => {
    fetchSubmissions();
    fetchCohorts();
    fetchAssignments();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/my-submissions`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions');
    }
  };

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/my-cohorts`);
      setCohorts(response.data);
    } catch (error) {
      console.error('Failed to load classes');
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/my-assignments`);
      setAssignments(response.data);
    } catch (error) {
      console.error('Failed to load assignments');
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/cohorts/join?join_code=${joinCode.toUpperCase()}`);
      toast.success('Successfully joined class!');
      setShowJoinClass(false);
      setJoinCode('');
      fetchCohorts();
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join class');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
              The Case Verse
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/cases" className="text-slate-700 hover:text-slate-900" data-testid="nav-cases-link">Case Library</Link>
              <Link to="/subscription" className="text-slate-700 hover:text-slate-900" data-testid="nav-subscription-link">Subscription</Link>
              {user?.role === 'admin' && <Link to="/admin" className="text-slate-700 hover:text-slate-900" data-testid="nav-admin-link">Admin</Link>}
              {['instructor', 'institution_admin', 'admin'].includes(user?.role) && (
                <>
                  <Link to="/book-training" className="text-slate-700 hover:text-slate-900" data-testid="nav-book-training-link">Book Training</Link>
                  <Link to="/request-case" className="text-slate-700 hover:text-slate-900" data-testid="nav-request-case-link">Request Case</Link>
                </>
              )}
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="logout-button">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-8" data-testid="page-title">Dashboard</h1>

          <div className="grid grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded border border-slate-200" data-testid="user-info-card">
              <h3 className="text-lg font-semibold mb-4">Account Information</h3>
              <div className="space-y-2 text-sm">
                <div><span className="text-slate-600">Name:</span> <span className="font-medium">{user?.name}</span></div>
                <div><span className="text-slate-600">Email:</span> <span className="font-medium">{user?.email}</span></div>
                <div><span className="text-slate-600">Role:</span> <span className="font-medium capitalize">{user?.role}</span></div>
              </div>
            </div>

            <div className="bg-white p-6 rounded border border-slate-200" data-testid="subscription-status-card">
              <h3 className="text-lg font-semibold mb-4">Subscription Status</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Status:</span>{' '}
                  <span className={`font-medium capitalize ${
                    user?.subscription_status === 'active' ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {user?.role === 'admin' ? 'active' : (user?.subscription_status || 'trial')}
                  </span>
                </div>
                {user?.is_trial && user?.trial_end && user?.role !== 'admin' && (
                  <div>
                    <span className="text-slate-600">Trial ends:</span>{' '}
                    <span className="font-medium">
                      {new Date(user.trial_end).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {user?.subscription_end && (
                  <div>
                    <span className="text-slate-600">Subscription ends:</span>{' '}
                    <span className="font-medium">
                      {new Date(user.subscription_end).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white p-6 rounded border border-slate-200" data-testid="access-card">
              <h3 className="text-lg font-semibold mb-4">Your Access</h3>
              <div className="space-y-2 text-sm">
                {user?.subscribed_product_types?.includes('all') ? (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    <span className="font-medium text-green-600">Full Platform Access</span>
                  </div>
                ) : user?.subscribed_product_types?.length > 0 ? (
                  user.subscribed_product_types.map((type, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                      <span>{type}</span>
                    </div>
                  ))
                ) : user?.role === 'admin' ? (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                    <span className="font-medium text-green-600">Full Platform Access (Admin)</span>
                  </div>
                ) : (
                  <p className="text-slate-600">Sample cases only (Free trial)</p>
                )}
              </div>
            </div>
          </div>

          {user?.is_trial && user?.role !== 'admin' && (
            <div className="bg-orange-50 border border-orange-200 rounded p-6" data-testid="upgrade-prompt">
              <h3 className="text-lg font-semibold mb-2">Upgrade Your Account</h3>
              <p className="text-slate-600 mb-4">
                You're currently on a free trial. Subscribe to get full access to all case studies.
              </p>
              <Link
                to="/subscription"
                className="inline-block px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all"
                data-testid="upgrade-button"
              >
                View Subscription Plans
              </Link>
            </div>
          )}

          <div className="grid grid-cols-2 gap-8 mt-8">
            <div className="bg-white p-8 rounded border border-slate-200" data-testid="my-classes">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif font-bold">My Classes</h2>
                <button
                  onClick={() => setShowJoinClass(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                  data-testid="join-class-button"
                >
                  <Plus className="w-4 h-4" />
                  Join Class
                </button>
              </div>

              {showJoinClass && (
                <form onSubmit={handleJoinClass} className="mb-6 p-4 bg-slate-50 rounded border border-slate-200">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Enter Join Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                      placeholder="e.g., ABC12345"
                      required
                      data-testid="join-code-input"
                    />
                    <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
                      Join
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowJoinClass(false)}
                      className="px-4 py-2 border border-slate-300 rounded hover:border-slate-900"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {cohorts.length === 0 ? (
                <p className="text-slate-600">Not enrolled in any classes yet</p>
              ) : (
                <div className="space-y-3">
                  {cohorts.map((cohort) => (
                    <div key={cohort.id} className="p-4 border border-slate-200 rounded">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-orange-600" />
                        <div>
                          <div className="font-semibold">{cohort.name}</div>
                          <div className="text-sm text-slate-600">Instructor: {cohort.instructor_name}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white p-8 rounded border border-slate-200" data-testid="my-assignments">
              <h2 className="text-2xl font-serif font-bold mb-6">My Assignments</h2>
              {assignments.length === 0 ? (
                <p className="text-slate-600">No assignments yet</p>
              ) : (
                <div className="space-y-3">
                  {assignments.map((assignment) => {
                    const isSim = assignment.content_type === 'simulation';
                    const title = isSim ? assignment.simulation_title : assignment.case_title;
                    const targetHref = isSim ? `/simulations/${assignment.simulation_id}/play` : `/worksheet/${assignment.case_study_id}`;
                    return (
                    <div key={assignment.id} className="p-4 border border-slate-200 rounded hover:border-slate-900 transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="text-xs uppercase font-mono text-slate-500 mb-0.5">{isSim ? '🎮 Simulation' : '📄 Case Study'}</div>
                          <div className="font-semibold mb-1">{title}</div>
                          {assignment.due_date && (
                            <div className="text-sm text-slate-600">
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          assignment.submission_status === 'submitted' 
                            ? 'bg-green-100 text-green-700' 
                            : assignment.submission_status === 'draft'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {assignment.submission_status === 'submitted' ? 'Submitted' : 
                           assignment.submission_status === 'draft' ? 'Draft' : 'Not Started'}
                        </span>
                      </div>
                      {assignment.has_feedback && (
                        <div className="text-sm text-green-600 flex items-center gap-1 mb-2">
                          <ClipboardList className="w-4 h-4" />
                          Feedback available
                        </div>
                      )}
                      <Link
                        to={targetHref}
                        className="inline-block text-sm text-orange-600 hover:text-orange-700"
                      >
                        {isSim ? 'Launch simulation →' : (assignment.submission_status === 'submitted' ? 'View Submission' : 'Start Working →')}
                      </Link>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-8 rounded border border-slate-200 mt-8" data-testid="my-submissions">
            <h2 className="text-2xl font-serif font-bold mb-6">My Worksheet Submissions</h2>
            {submissions.length === 0 ? (
              <p className="text-slate-600">No worksheet submissions yet. Start working on case studies!</p>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <div key={submission.id} className="p-4 border border-slate-200 rounded hover:border-slate-900 transition-all">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{submission.case_title}</h3>
                        <div className="text-sm text-slate-600 space-x-4">
                          <span>Status: <span className={`font-medium ${submission.status === 'submitted' ? 'text-green-600' : 'text-orange-600'}`}>
                            {submission.status === 'submitted' ? 'Submitted' : 'Draft'}
                          </span></span>
                          {submission.submitted_at && (
                            <span>Submitted: {new Date(submission.submitted_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <Link
                        to={`/worksheet/${submission.case_study_id}`}
                        className="px-4 py-2 text-sm border border-slate-300 rounded hover:border-slate-900 transition-all"
                      >
                        {submission.status === 'submitted' ? 'View' : 'Continue'}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}